import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Flame, TrendingUp, Gift } from 'lucide-react'
import { useQuery } from '@tanstack/react-query'
import TopHeader from '../components/layout/TopHeader.jsx'
import { ProductCard, ProductCardSkeleton, SectionHeader, getCategoryEmoji } from '../components/ui/index.jsx'
import { productService, categoryService } from '../services/api.js'
import { useAppStore, useAuthStore } from '../store/index.js'
import api from '../services/api.js'

// ── Banner slides — same paths as customer frontend ────────────────────────────
const HERO_SLIDES = [
  { src: '/images/banners/banner-hero-0.webp', label: "Tony's Liquor", sub: 'Bulk B2B Distributor · Pretoria' },
  { src: '/images/banners/banner-whisky-1.webp', label: 'Premium Whisky', sub: 'Scotch, Bourbon & more' },
  { src: '/images/banners/banner-wine-1.webp',   label: 'Fine Wines',     sub: 'Red, White & Sparkling' },
  { src: '/images/banners/banner-hero-1.webp',   label: 'Wholesale Prices', sub: 'Direct from distributor' },
]

function HeroBanner() {
  const [active, setActive] = useState(0)
  const [loaded, setLoaded] = useState({})
  useEffect(() => {
    const t = setInterval(() => setActive(i => (i + 1) % HERO_SLIDES.length), 4500)
    return () => clearInterval(t)
  }, [])
  return (
    <div className="relative mx-4 h-44 rounded-2xl overflow-hidden mb-2 bg-gradient-to-br from-surface-elevated to-surface-dark">
      {HERO_SLIDES.map(({ src, label, sub }, i) => (
        <div
          key={src}
          className={`absolute inset-0 transition-opacity duration-700 ${i === active ? 'opacity-100' : 'opacity-0'}`}
        >
          <img
            src={src}
            alt={label}
            className="w-full h-full object-cover"
            onLoad={() => setLoaded(p => ({ ...p, [i]: true }))}
            onError={e => { e.currentTarget.style.display = 'none' }}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
          <div className="absolute bottom-4 left-5">
            <p className="font-heading text-white text-xl leading-tight drop-shadow">{label}</p>
            <p className="font-display text-gray-300 text-xs mt-0.5 drop-shadow">{sub}</p>
          </div>
        </div>
      ))}
      {/* Dot indicators */}
      <div className="absolute bottom-3 right-3 flex gap-1">
        {HERO_SLIDES.map((_, i) => (
          <button
            key={i}
            onClick={() => setActive(i)}
            className={`h-1.5 rounded-full transition-all duration-300 ${i === active ? 'w-5 bg-gold' : 'w-1.5 bg-white/30'}`}
          />
        ))}
      </div>
    </div>
  )
}

// ── Full CAT_ICONS map (30+ categories) ───────────────────────────────────────
const CAT_ICONS = {
  whisky: '🥃', whiskey: '🥃', bourbon: '🥃', scotch: '🥃',
  wine: '🍷', 'red wine': '🍷', 'white wine': '🍷', 'rosé': '🍷',
  vodka: '🍸',
  beer: '🍺', 'craft beer': '🍻',
  gin: '🌿',
  rum: '🏝️',
  brandy: '🍂', cognac: '🍂',
  liqueur: '✨',
  tequila: '🌵',
  'soft drinks': '💧', 'mineral drinks': '💧', water: '🫧',
  cider: '🍋',
  champagne: '🥂', 'champagne & sparkling wine': '🥂', sparkling: '🥂',
  'rtd cocktail': '🍹', rtd: '🥤', 'r t d cocktails': '🍹', 'r t ds': '🥤',
  aperitif: '🫗', aperitifs: '🫗',
  cane: '🌾',
  shooters: '🔫',
  'port sherry muscadel etc': '🫙', port: '🫙', sherry: '🫙',
  energy: '⚡', 'energy drinks': '⚡',
  juice: '🍊',
  cordials: '🍓',
  tobacco: '🚬', cigarettes: '🚬',
  glassware: '🥂',
  snacks: '🍿',
  gifts: '🎁',
  spirits: '🥃',
  'box wine': '📦',
}

// Resolve icon for a category name
function catIcon(name) {
  if (!name) return '🍶'
  const k = name.toLowerCase().trim()
  if (CAT_ICONS[k]) return CAT_ICONS[k]
  // partial match
  for (const [key, icon] of Object.entries(CAT_ICONS)) {
    if (k.includes(key) || key.includes(k)) return icon
  }
  return '🍶'
}

const CAT_COLORS = {
  whisky: '#8B4513', wine: '#722F37', vodka: '#4169E1', beer: '#DAA520',
  gin: '#228B22', rum: '#8B6914', brandy: '#800020', 'soft drinks': '#FF6347',
  cider: '#4CAF50', champagne: '#C0A060', liqueur: '#9C27B0', tequila: '#FF9800',
  energy: '#F44336', default: '#0e947a',
}
function catColor(name) {
  const k = (name || '').toLowerCase().trim()
  return CAT_COLORS[k] || CAT_COLORS.default
}

// ── Alcohol category names (for filtering) ────────────────────────────────────
function CategoryScroll({ categories }) {
  const navigate = useNavigate()
  const setCategory = useAppStore(s => s.setCategory)
  return (
    <div className="flex gap-3 overflow-x-auto scroll-hide px-4 pb-1">
      {categories.map(cat => {
        const icon = catIcon(cat.name)
        const color = catColor(cat.name)
        return (
          <button key={cat.id} onClick={() => { setCategory(cat.name); navigate('/shop') }}
            className="flex flex-col items-center gap-1.5 flex-shrink-0 active:scale-95 transition-transform duration-150">
            <div className="w-14 h-14 rounded-2xl flex items-center justify-center"
              style={{ background: `${color}22`, border: `1px solid ${color}44` }}>
              <span className="text-2xl">{icon}</span>
            </div>
            <span className="text-[11px] font-display text-gray-300 whitespace-nowrap max-w-[3.5rem] truncate">{cat.name}</span>
          </button>
        )
      })}
    </div>
  )
}

// Fixed norm() — proper image resolution
function norm(p) {
  const image = p.primary_image
    ? p.primary_image
    : p.image || null
  return {
    id: p.id,
    name: p.description || p.stock_code,
    price: p.inclprice ?? p.inclprice2 ?? 0,
    originalPrice: null,
    rating: p.average_rating ?? null,
    reviews: p.review_count ?? 0,
    inStock: true,
    badge: null,
    image,
    primary_image: p.primary_image || null,
    stock_code: p.stock_code,
    brand: p.brand || null,
    category_name: p.category_name || null,
  }
}

export default function Home() {
  const navigate = useNavigate()
  const { isAuthenticated, user, setCustomer } = useAuthStore()
  const [subState, setSubState] = useState('idle') // idle | loading | done | error

  const handleSubscribe = async () => {
    if (!isAuthenticated) { navigate('/login'); return }
    setSubState('loading')
    try {
      await api.post('/customer/subscribe')
      setSubState('done')
    } catch {
      setSubState('error')
    }
  }

  const { data: pd, isLoading: pl } = useQuery({ queryKey: ['products', 'home'], queryFn: () => productService.getFeatured(12), staleTime: 5 * 60 * 1000 })
  const { data: cd } = useQuery({ queryKey: ['categories'], queryFn: categoryService.getAll, staleTime: 10 * 60 * 1000 })

  const raw = Array.isArray(pd) ? pd : (pd?.items ?? pd?.products ?? [])
  const products = raw.map(norm)
  const featured = products.slice(0, 6)
  const rest = products.slice(6, 12)
  const cats = Array.isArray(cd) ? cd : (cd?.items ?? cd?.categories ?? [])

  return (
    <div className="page-container bg-surface-dark">
      <TopHeader showLogo />
      <div className="space-y-5 pt-2 pb-4">
        <HeroBanner />
        <div className="flex gap-2 px-4">
          {[
            { icon: Flame, label: 'Hot Deals', color: 'text-orange-400', bg: 'bg-orange-400/10' },
            { icon: TrendingUp, label: 'Trending', color: 'text-blue-400', bg: 'bg-blue-400/10' },
            { icon: Gift, label: 'Gifts', color: 'text-purple-400', bg: 'bg-purple-400/10' },
          ].map(({ icon: Icon, label, color, bg }) => (
            <button key={label} className={`flex items-center gap-1.5 ${bg} rounded-xl px-3 py-2 flex-1 justify-center active:opacity-70`}>
              <Icon size={14} className={color} />
              <span className={`text-xs font-display font-semibold ${color}`}>{label}</span>
            </button>
          ))}
        </div>
        {cats.length > 0 && (
          <div>
            <SectionHeader title="Categories" action={() => navigate('/shop')} />
            <CategoryScroll categories={cats} />
          </div>
        )}
        <div>
          <SectionHeader title="Featured Products" action={() => navigate('/shop')} />
          <div className="px-4 grid grid-cols-2 gap-3">
            {pl
              ? Array(4).fill(0).map((_, i) => <ProductCardSkeleton key={i} />)
              : featured.map(p => <ProductCard key={p.id} product={p} onClick={() => navigate(`/product/${p.id}`)} />)}
          </div>
        </div>

        {/* Subscribe to Deals banner */}
        <div className="mx-4">
          <div className="bg-gradient-to-r from-primary-dark to-primary rounded-2xl p-4">
            <div className="mb-3">
              <p className="font-heading text-white text-base leading-tight">Get exclusive deals! 🔔</p>
              <p className="font-display text-green-100 text-xs mt-1">Subscribe for special offers, new arrivals and bulk pricing.</p>
            </div>
            {subState === 'done' ? (
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-white/20 text-white text-xs font-display font-semibold">
                ✅ You're subscribed!
              </div>
            ) : (
              <button
                onClick={handleSubscribe}
                disabled={subState === 'loading'}
                className="btn-gold py-2 px-4 text-xs flex items-center gap-1.5 disabled:opacity-60"
              >
                {subState === 'loading'
                  ? <><span className="w-3.5 h-3.5 border-2 border-gray-400 border-t-gray-800 rounded-full animate-spin" />Subscribing…</>
                  : subState === 'error' ? '⚠ Try again'
                  : '🔔 Subscribe to Deals'}
              </button>
            )}
          </div>
        </div>

        {!pl && rest.length > 0 && (
          <div>
            <SectionHeader title="More Products" action={() => navigate('/shop')} actionLabel="View all" />
            <div className="px-4 grid grid-cols-2 gap-3">
              {rest.map(p => <ProductCard key={p.id} product={p} onClick={() => navigate(`/product/${p.id}`)} />)}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
